import {useMemo, useState} from 'react';
import type {QueryResult} from '@apollo/react-common';
import {useMutation, useQuery} from '@apollo/react-hooks';
import {useHistory, useLocation, useRouteMatch} from 'react-router-dom';
import {
  EXPIRED_MAGIC_LINK_PATH,
  PROGRESS_PATH,
  REQUEST_SIGN_IN_LINK_PATH,
  SELF_ASSIGN_PATH,
} from '../../paths';
import type {
  MagicLinkDriverSessionCreateMutation,
  MagicLinkDriverSessionCreateMutationVariables,
  MeQuery,
} from '../graphql/types';
import {SelfAssignSource} from '../graphql/types';
import {MagicLinkDriverSessionCreate, Me} from '../graphql';
import configureUserMonitoring from './configureUserMonitoring';

const MAGIC_LINK_TOKEN_QUERY_PARAM = 't';

type MeQueryHookResult = QueryResult<MeQuery, unknown>;

type UseIdentityQueryResult = Pick<MeQueryHookResult, 'error'> & {
  identity: MeQuery['me'];
  isLoading: MeQueryHookResult['loading'];
};

type UseIdentityQueryOptions = {
  isAuthRequired?: boolean;
};

const useAuth = ({isAuthRequired}: UseIdentityQueryOptions): UseIdentityQueryResult => {
  const location = useLocation();
  const history = useHistory();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const [isLoading, setIsLoading] = useState(true);

  const driverAssignmentMatch = useRouteMatch<{driverAssignmentId: string}>(PROGRESS_PATH);
  const driverAssignmentId = driverAssignmentMatch?.params?.driverAssignmentId;
  const selfAssignDeliveryMatch = useRouteMatch<{deliveryId: string}>(SELF_ASSIGN_PATH);
  const deliveryId = selfAssignDeliveryMatch?.params?.deliveryId;
  const source = searchParams.get('source') as SelfAssignSource | null;
  const isSelfAssign = !!deliveryId && (source === 'qrcode' || source === 'sms');

  const navigateToSignIn = (): void => {
    const redirectParams = isSelfAssign ? `?deliveryId=${deliveryId}&source=${source}` : '';
    history.push(`${REQUEST_SIGN_IN_LINK_PATH}${redirectParams}`);
  };

  const navigateToSelfAssignRegistration = (): void => {
    const redirectParams = source ? `?source=${source}` : '';
    history.push(`/self_assign_registration/${deliveryId}${redirectParams}`);
  };

  const navigateToExpiredMagicLink = (): void => {
    if (driverAssignmentId) {
      return history.push(`${EXPIRED_MAGIC_LINK_PATH}?driverAssignmentId=${driverAssignmentId}`);
    }

    if (deliveryId || source) {
      return history.push(`${EXPIRED_MAGIC_LINK_PATH}?deliveryId=${deliveryId}&source=${source}`);
    }

    history.push(EXPIRED_MAGIC_LINK_PATH);
  };

  const stripMagicLinkTokenFromUrl = (): void => {
    searchParams.delete(MAGIC_LINK_TOKEN_QUERY_PARAM);
    history.replace({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  const [createMagicLinkSession, createMagicLinkSessionQueryResult] = useMutation<
    MagicLinkDriverSessionCreateMutation,
    MagicLinkDriverSessionCreateMutationVariables
  >(MagicLinkDriverSessionCreate, {
    onCompleted: result => {
      if (result.magicLinkDriverSessionCreate?.success) {
        meQueryResult.refetch();
      } else {
        navigateToExpiredMagicLink();
        setIsLoading(false);
      }
    },
    onError: () => {
      navigateToExpiredMagicLink();
      setIsLoading(false);
    },
  });

  const meQueryResult = useQuery<MeQuery>(Me, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onCompleted: ({me}) => {
      const magicLinkToken = searchParams.get(MAGIC_LINK_TOKEN_QUERY_PARAM);
      const shouldCreateSession = (isAuthRequired || isSelfAssign) && !!magicLinkToken;
      // Due to the way QR codes are generated in ezrails, drivers can't be sent
      // a link that contains a magic link token. We account for that here by
      // directing them to the page to request a magic link that does contain a
      // token.
      const isMagicLinkNeeded = (isAuthRequired || isSelfAssign) && !magicLinkToken;

      configureUserMonitoring(me);

      if (magicLinkToken) {
        stripMagicLinkTokenFromUrl();
      }

      if (me) {
        if (isSelfAssign && (!me.firstName || !me.lastName)) {
          navigateToSelfAssignRegistration();
        }
      } else if (shouldCreateSession) {
        createMagicLinkSession({variables: {input: {token: magicLinkToken}}});
        return;
      } else if (isMagicLinkNeeded) {
        navigateToSignIn();
      }

      setIsLoading(false);
    },
    onError: () => {
      if (searchParams.get(MAGIC_LINK_TOKEN_QUERY_PARAM)) {
        stripMagicLinkTokenFromUrl();
      }

      setIsLoading(false);
    },
  });

  return {
    error: meQueryResult.error || createMagicLinkSessionQueryResult.error,
    identity: meQueryResult.data?.me,
    isLoading,
  };
};

export default useAuth;
